/**
 * Styles for react-select, built from the shadcn design tokens.
 *
 * Used in the `styles` prop of every Select in the app, so that the shared
 * pickers (`ProviderList`, `ClientsSearchList`, `LockersSearchList`, the
 * incident selects…) look like the `Field` control next to them instead of like
 * react-select's own default. It used to override nothing in light mode, which
 * is why a migrated form showed a rounded, tinted input beside a squared white
 * one with a boxed icon glued to its left.
 *
 * Tokens are read from the document rather than hardcoded, so the light/dark
 * swap costs nothing here: the theme provider rewrites the custom properties
 * and the next call picks them up. The first paint may come from the inline
 * script in index.html, so `theme` is passed in rather than sniffed from the
 * DOM, and the toggle applies instantly.
 */

const raw = (varName, fallback) => {
  if (typeof document === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return value || fallback;
};

/** shadcn tokens are bare HSL triplets ("228 69% 65%"), not colours. */
const hsl = (varName, fallback, alpha) => {
  const value = raw(varName, fallback);
  return alpha === undefined ? `hsl(${value})` : `hsl(${value} / ${alpha})`;
};

/**
 * @param {"light"|"dark"} theme  current theme; only used to re-run the lookup.
 * @param {{hasIcon?: boolean}} [options]  `hasIcon` leaves room for a glyph
 *        rendered inside the control, replacing Bootstrap's `input-group-prepend`.
 */
export const getReactSelectStyles = (theme = "light", options = {}) => {
  const { hasIcon = false } = options;

  const background = hsl("--background", "0 0% 100%");
  const foreground = hsl("--foreground", "228 24% 12%");
  const muted = hsl("--muted", "228 24% 96%", 0.6);
  const mutedSolid = hsl("--muted", "228 24% 96%");
  const mutedFg = hsl("--muted-foreground", "228 12% 44%");
  const placeholderFg = hsl("--muted-foreground", "228 12% 44%", 0.7);
  const primary = hsl("--primary", "228 69% 65%");
  const primaryFg = hsl("--primary-foreground", "0 0% 100%");
  const ring = hsl("--primary", "228 69% 65%", 0.15);
  const border = hsl("--border", "228 20% 90%");
  const popover = hsl("--popover", "0 0% 100%");
  const accent = hsl("--accent", "228 32% 95%");

  return {
    container: (provided) => ({
      ...provided,
      width: "100%",
      flex: 1,
    }),
    // Mirrors FIELD_SKIN in components/ui/form-field.jsx: 2.75rem tall, 0.5rem
    // radius, tinted fill, no visible border until focus, wide brand halo.
    control: (provided, state) => ({
      ...provided,
      minHeight: "2.75rem",
      borderRadius: "0.5rem",
      fontSize: "0.875rem",
      backgroundColor: state.isDisabled ? mutedSolid : state.isFocused ? background : muted,
      borderColor: state.isFocused ? primary : "transparent",
      boxShadow: state.isFocused ? `0 0 0 4px ${ring}` : "none",
      color: foreground,
      transition: "border-color 150ms, box-shadow 150ms, background-color 150ms",
      "&:hover": { borderColor: state.isFocused ? primary : "transparent" },
    }),
    valueContainer: (provided) => ({
      ...provided,
      paddingLeft: hasIcon ? "2.25rem" : "0.75rem",
      paddingRight: "0.25rem",
    }),
    singleValue: (provided) => ({ ...provided, color: foreground }),
    input: (provided) => ({ ...provided, color: foreground }),
    placeholder: (provided) => ({ ...provided, color: placeholderFg }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: accent,
      borderRadius: "0.375rem",
    }),
    multiValueLabel: (provided) => ({ ...provided, color: foreground }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: mutedFg,
      ":hover": { backgroundColor: primary, color: primaryFg },
    }),
    // The vertical rule belonged to the boxed look; the new field has none.
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: mutedFg,
      ":hover": { color: foreground },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: mutedFg,
      ":hover": { color: foreground },
    }),
    menu: (provided) => ({
      ...provided,
      // Above the glyph SelectShell paints over the control (z-index 1).
      zIndex: 2,
      backgroundColor: popover,
      border: `1px solid ${border}`,
      borderRadius: "0.5rem",
      overflow: "hidden",
      boxShadow: "0 12px 32px -8px rgb(0 0 0 / 0.25)",
    }),
    menuList: (provided) => ({ ...provided, backgroundColor: popover, padding: "0.25rem" }),
    option: (provided, state) => ({
      ...provided,
      borderRadius: "0.375rem",
      fontSize: "0.875rem",
      cursor: "pointer",
      backgroundColor: state.isSelected ? primary : state.isFocused ? accent : "transparent",
      color: state.isSelected ? primaryFg : foreground,
      ":active": { backgroundColor: state.isSelected ? primary : accent },
    }),
    noOptionsMessage: (provided) => ({ ...provided, color: mutedFg, fontSize: "0.875rem" }),
    loadingMessage: (provided) => ({ ...provided, color: mutedFg, fontSize: "0.875rem" }),
    // Keeps the menu above cards that clip their overflow.
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  };
};
